import React from 'react';
import PropTypes from 'prop-types';
import { Stack, Box, InputAdornment } from '@mui/material';
import { CssTextField } from 'components/select';
import { useRecoilState } from 'recoil';
import { OrderPayload } from 'pages/myidm/store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import HelpImg from 'pages/provisioning/_components/helpImg';
import { validateGroupName } from 'pages/myidm//utils/validation';

const GroupName = ({ index }) => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [suffixValue, setSuffixValue] = React.useState(''); // Store only the suffix part
    const [order, setOrder] = useRecoilState(OrderPayload);
    const inputRef = React.useRef(null);
    const [showHelperText, setHelperText] = React.useState(false);
    const [showHelperMsg, setHelperMsg] = React.useState('');

    // Get the current resource group data
    const currentGroup = index !== undefined ? order?.multipleResourceGroups?.[index] : order;

    // Get fixed prefix based on GroupCategory, GroupSubCategory and CloudType
    const getPrefix = () => {
        const category = currentGroup?.groupCategory || '';
        const subCategoryBackendName = currentGroup?.groupSubCategoryDetails?.backendName || '';
        const cloudType = currentGroup?.cloudType || '';

        if (!category || !subCategoryBackendName || !cloudType) {
            return '';
        }

        return `${category}.${subCategoryBackendName}.${cloudType}.`;
    };

    const prefix = getPrefix();

    // Generate placeholder text
    const getPlaceholder = () => {
        if (!prefix) {
            return 'Select Group Category, Sub Category and Cloud Type first';
        }

        return `----.----.----.----`;
    };

    const setGroupName = (groupName) => {
        const updatedGroups = [...(order?.multipleResourceGroups || [])];
        updatedGroups[index] = {
            ...updatedGroups[index],
            groupName,
        };
        setOrder({
            ...order,
            multipleResourceGroups: updatedGroups,
        });
    };

    const handleOnChange = (event) => {
        const newValue = event.target.value;
        const cursorPosition = event.target.selectionStart;

        // If prefix doesn't exist yet, don't allow input
        if (!prefix) {
            return;
        }

        // Validate suffix: only allow alphanumeric characters and dots
        // Remove any special characters but keep dots
        let cleanSuffix = newValue.replace(/[^a-zA-Z0-9.]/g, '');

        // Prevent consecutive dots (..)
        cleanSuffix = cleanSuffix.replace(/\.{2,}/g, '.');

        // Don't allow starting with a dot
        if (cleanSuffix.startsWith('.')) {
            cleanSuffix = cleanSuffix.substring(1);
        }

        // Calculate new cursor position
        // Count how many characters were removed before cursor
        const removedCharsBeforeCursor = newValue.substring(0, cursorPosition).replace(/[a-zA-Z0-9.]/g, '').length;
        let newCursorPosition = cursorPosition - removedCharsBeforeCursor;

        // Adjust cursor if consecutive dots were removed
        const originalDotsBeforeCursor = (newValue.substring(0, cursorPosition).match(/\./g) || []).length;
        const newDotsBeforeCursor = (cleanSuffix.substring(0, newCursorPosition).match(/\./g) || []).length;
        if (originalDotsBeforeCursor > newDotsBeforeCursor) {
            newCursorPosition = Math.max(0, newCursorPosition - (originalDotsBeforeCursor - newDotsBeforeCursor));
        }

        setSuffixValue(cleanSuffix);
        const finalValue = prefix + cleanSuffix;
        setGroupName(finalValue);

        // Restore cursor position after React re-renders
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
            }
        }, 0);
    };

    // Initialize input value with suffix when category/subcategory selected
    React.useEffect(() => {
        if (prefix && currentGroup?.groupName && currentGroup.groupName.startsWith(prefix)) {
            // Extract suffix from saved group name
            const suffix = currentGroup.groupName.substring(prefix.length);
            setSuffixValue(suffix);
        } else {
            setSuffixValue('');
        }
    }, [prefix, currentGroup?.groupName]);

    React.useEffect(() => {
        if (!currentGroup?.groupName && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [currentGroup?.groupName, order?.isTouched]);

    // Validate group name and update helper text
    React.useEffect(() => {
        const validation = validateGroupName(currentGroup?.groupName, prefix, order?.multipleResourceGroups, index);
        if (!validation.isValid && currentGroup?.groupName) {
            setHelperText(true);
            setHelperMsg(validation.message);
        } else {
            setHelperText(false);
            setHelperMsg('');
        }
    }, [currentGroup?.groupName, prefix, order?.multipleResourceGroups, index]);

    // Reset group name when category, subcategory or cloud type changes
    React.useEffect(() => {
        if (currentGroup?.groupName && prefix) {
            // If the current group name doesn't match the new prefix, reset it
            if (!currentGroup.groupName.startsWith(prefix)) {
                setSuffixValue('');
                setGroupName('');
            }
        }
    }, [currentGroup?.groupCategory, currentGroup?.groupSubCategoryDetails, currentGroup?.cloudType]);

    const isDisabled = !currentGroup?.groupCategory || !currentGroup?.groupSubCategoryDetails?.backendName || !currentGroup?.cloudType;

    const validateInput = () => {
        if (
            (isTouched && order?.isTouched && !validateGroupName(currentGroup?.groupName, prefix, order?.multipleResourceGroups, index).isValid) ||
            showHelperText
        ) {
            return 'validate';
        }
        return '';
    };

    const getHelperText = () => {
        if (isDisabled) {
            return 'Please select Group Category, Sub Category and Cloud Type first';
        }
        if (isTouched && (!currentGroup?.groupName || currentGroup?.groupName === prefix)) {
            return 'Group Name is required';
        }
        return 'The Active Directory group common name has a maximum character limit of 64 characters, which must not be exceeded. Please ensure you follow the required prerequisites for Group Category, Group Sub Category, Cloud Type, and Active Directory Group Type. The overall group name must not exceed 64 characters.';
    };

    return (
        <Stack direction="row" spacing={1} sx={ProvisioningStyles.stack}>
            <CssTextField
                inputRef={inputRef}
                label="Group Name *"
                name="groupName"
                value={suffixValue}
                onChange={handleOnChange}
                placeholder={getPlaceholder()}
                fullWidth
                size="small"
                disabled={isDisabled}
                error={showHelperText || (isTouched && (!currentGroup?.groupName || currentGroup?.groupName === prefix))}
                helperText={showHelperText && <span style={ProvisioningStyles.disposal?.helpText}>{showHelperMsg}</span>}
                className={validateInput()}
                InputProps={{
                    startAdornment: prefix ? (
                        <InputAdornment position="start" sx={{ marginRight: 0, marginLeft: '-4px' }}>
                            <Box
                                component="span"
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    color: isDisabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                                }}
                            >
                                {prefix}
                            </Box>
                        </InputAdornment>
                    ) : null,
                }}
            />
            <HelpImg title={getHelperText()} />
        </Stack>
    );
};

GroupName.propTypes = {
    index: PropTypes.number,
};

GroupName.defaultProps = {
    index: undefined,
};

export default GroupName;
